import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import AuthStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';
import { LogLevel } from 'utils/logger';

import { brand, env, getCurrentCommitHash, isSSG } from '../environment';

import { IDatadogLogger } from './types';

const UNKNOWN_ERROR_MESSAGE: string = 'Unknown application error. Unable to parse error message.';

export function dataDogLogger({ message, context = {}, status }: IDatadogLogger): void {
  if (isSSG) {
    return;
  }
  const currentEnv = env();
  const ctx = {
    appVersionCode: getCurrentCommitHash(),
    commit: getCurrentCommitHash(),
    brand: brand(),
    platform: 'web',
    stage: currentEnv,
    userId: AuthStorage?.getItem(StorageKeys.USER_AUTH_TOKEN) ?? undefined,
    ...context,
  };

  let backendFeedback: string;

  try {
    if (typeof message === 'string') {
      backendFeedback = message;
    } else if (typeof message?.message === 'string') {
      backendFeedback = message.message;
    } else if (typeof message?.toString === 'function') {
      backendFeedback = message.toString();
    } else {
      backendFeedback = UNKNOWN_ERROR_MESSAGE;
    }
  } catch (error) {
    backendFeedback = UNKNOWN_ERROR_MESSAGE;
  }

  switch (status) {
    case LogLevel.trace:
    case LogLevel.debug:
      datadogLogs.logger.debug(backendFeedback, ctx);
      break;
    case LogLevel.info:
      datadogLogs.logger.info(backendFeedback, ctx);
      break;
    case LogLevel.warn:
      datadogLogs.logger.warn(backendFeedback, ctx);
      break;
    case LogLevel.error:
    case LogLevel.fatal:
      // @ts-ignore
      const error = ctx && ctx.error;
      // Filter out expected AbortError from Apollo fetch cancellations
      if (
        error &&
        (error.name === 'AbortError' ||
          error.message?.includes('AbortError') ||
          backendFeedback?.includes('AbortError'))
      ) {
        return; // skip sending to Datadog
      }
      datadogRum.addError(error || backendFeedback, ctx);
      break;
    default:
      void datadogLogs.logger.warn(`Log level ${status} not supported`);
  }
}

export function addContext(key: string, value: string) {
  datadogLogs.logger.setContextProperty(key, value);
}

export function setUser(user: { id: string }) {
  if (user.id) {
    datadogRum.setUser(user);
  } else {
    datadogRum.clearUser();
  }
}
